export enum AlignEnum {
	'baseline' = 'baseline',
	'center' = 'center',
	'end' = 'end',
	'flex-end' = 'flex-end',
	'flex-start' = 'flex-start',
	'inherit' = 'inherit',
	'initial' = 'initial',
	'normal' = 'normal',
	'start' = 'start',
	'stretch' = 'stretch',
	'unset' = 'unset',
}

export interface AlignInterface {
	/** The `align` prop sets the align-items styling */
	align?: AlignEnum;
}

export enum BorderStyleEnum {
	'none' = 'none',
	'hidden' = 'hidden',
	'dotted' = 'dotted',
	'dashed' = 'dashed',
	'solid' = 'solid',
	'double' = 'double',
	'groove' = 'groove',
	'ridge' = 'ridge',
	'inset' = 'inset',
	'outset' = 'outset',
}

export interface BorderInterface {
	/** The `b` prop sets the border style  */
	b?: string;
	/** The `bc` prop sets the border-color style  */
	bc?: string;
	/** The `bs` prop sets the border-style style  */
	bs?: BorderStyleEnum;
	/** The `bx` prop sets the horizontal (left, right) border style  */
	bx?: string | number;
	/** The `by` prop sets the vertical (top, bottom) border style  */
	by?: string | number;
	/** The `bt` prop sets the border-top style  */
	bt?: string | number;
	/** The `br` prop sets the border-right style  */
	br?: string | number;
	/** The `bb` prop sets the border-bottom style  */
	bb?: string | number;
	/** The `bl` prop sets the border-left style  */
	bl?: string | number;
}

export interface BorderRadiusInterface {
	/** The `radius` prop sets the border radius style  */
	radius?: string | number;
}

export interface ClassNameInterface {
	/** The `className` prop should be available to all components */
	className?: string;
}

export type ElevationEnum = 5 | 4 | 3 | 2 | 1 | 0 | -1 | -2 | -3;

export interface ElevationInterface {
	/** The `elevation` prop sets the box-shadow styling of an element */
	elevation?: ElevationEnum;
}

export enum FlexDirectionsEnum {
	'column' = 'column',
	'column-reverse' = 'column-reverse',
	'row' = 'row',
	'row-reverse' = 'row-reverse',
}

export interface FlexInterface {
	/** The `flex` prop sets the display to flex styling as well as the flex-direction of an element */
	flex?: FlexDirectionsEnum;
}

export type FontWeightEnum =
	| 'normal'
	| 'bold'
	| 'lighter'
	| 'bolder'
	| '100'
	| '200'
	| '300'
	| '400'
	| '500'
	| '600'
	| '700'
	| '800'
	| '900';

export interface FontWeightInterface {
	/** The `weight` prop sets the font-weight styling of an element */
	weight?: FontWeightEnum;
}

export interface HeightInterface {
	/** The `height` prop sets the height style */
	height?: string | number;
	/** The `minH` prop sets the min-height style */
	minH?: string | number;
	/** The `maxH` prop sets the max-height style */
	maxH?: string | number;
}

export interface IdInterface {
	id: string;
}

export type IntentEnum = 'error' | 'info' | 'success' | 'warning';

export interface IntentInterface {
	/** The `intent` prop sets the styles based on a css variable. */
	intent?: IntentEnum;
}

export enum JustifyEnum {
	'center' = 'center',
	'end' = 'end',
	'flex-end' = 'flex-end',
	'flex-start' = 'flex-start',
	'inherit' = 'inherit',
	'initial' = 'initial',
	'left' = 'left',
	'normal' = 'normal',
	'right' = 'right',
	'space-around' = 'space-around',
	'space-between' = 'space-between',
	'space-evenly' = 'space-evenly',
	'start' = 'start',
	'stretch' = 'stretch',
	'unset' = 'unset',
}

export interface JustifyInterface {
	/** The `justify` prop sets the justify-content styling */
	justify?: JustifyEnum;
}

export interface MarginInterface {
	/** The `m` prop sets the margin style  */
	m?: string | number;
	/** The `mx` prop sets the horizontal (left, right) margin style  */
	mx?: string | number;
	/** The `my` prop sets the vertical (top, bottom) margin style  */
	my?: string | number;
	/** The `mt` prop sets the margin-top style  */
	mt?: string | number;
	/** The `mr` prop sets the margin-right style  */
	mr?: string | number;
	/** The `mb` prop sets the margin-bottom style  */
	mb?: string | number;
	/** The `ml` prop sets the margin-left style  */
	ml?: string | number;
}

export interface PaddingInterface {
	/** The `p` prop sets the padding style  */
	p?: string | number;
	/** The `px` prop sets the horizontal (left, right) padding style  */
	px?: string | number;
	/** The `py` prop sets the vertical (top, bottom) padding style  */
	py?: string | number;
	/** The `pt` prop sets the padding-top style  */
	pt?: string | number;
	/** The `pr` prop sets the padding-right style  */
	pr?: string | number;
	/** The `pb` prop sets the padding-bottom style  */
	pb?: string | number;
	/** The `pl` prop sets the padding-left style  */
	pl?: string | number;
}

export type PositionEnum =
	| 'absolute'
	| 'fixed'
	| 'relative'
	| 'static'
	| 'sticky';

export interface PositionInterface {
	bottom?: string | number;
	left?: string | number;
	/** The `position` prop is for setting the position styling */
	position?: PositionEnum;
	right?: string | number;
	top?: string | number;
}

export interface SquareSizeInterface {
	/** The `size` prop sets both the height and width style */
	size?: string | number;
	/** The `minS` prop sets both the min-height and min-width style */
	minS?: string | number;
	/** The `maxS` prop sets both the max-height and max-width style */
	maxS?: string | number;
}

export interface TestId {
	/** The `testId` prop is for attaching a data-testid to an element for testing */
	testId?: string;
}

export type TextElementEnum =
	| `small`
	| 'span'
	| 'p'
	| 'h1'
	| 'h2'
	| 'h3'
	| 'h4'
	| 'h5'
	| 'h6';

export interface OnClickOutsideInterface {
	close: () => void;
	open: boolean;
	shouldCloseOnClickOutside?: boolean;
}

export interface WidthInterface {
	/** The `width` prop sets the width style */
	width?: string | number;
	/** The `minW` prop sets the min-width style */
	minW?: string | number;
	/** The `maxW` prop sets the max-width style */
	maxW?: string | number;
}
